interface StarRatingProps {
  value: number;
  count?: number;
  size?: "sm" | "md" | "lg";
  showValue?: boolean;
  className?: string;
}

const SIZE_CLASS: Record<"sm" | "md" | "lg", string> = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-xl",
};

/**
 * Muestra una calificación en estrellas (soporta media estrella) con la
 * cantidad de valoraciones de forma opcional.
 */
export default function StarRating({
  value,
  count,
  size = "sm",
  showValue = false,
  className = "",
}: StarRatingProps) {
  const rounded = Math.round(value * 2) / 2;
  const starSize = SIZE_CLASS[size];

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <span className={`flex items-center gap-0.5 ${starSize} text-accent-500`}>
        {[1, 2, 3, 4, 5].map((star) => {
          if (rounded >= star) {
            return <i key={star} className="ri-star-fill"></i>;
          }
          if (rounded >= star - 0.5) {
            return <i key={star} className="ri-star-half-fill"></i>;
          }
          return <i key={star} className="ri-star-line text-foreground-300"></i>;
        })}
      </span>
      {showValue && (
        <span className="font-semibold text-foreground-800">
          {value > 0 ? value.toFixed(1) : "—"}
        </span>
      )}
      {typeof count === "number" && (
        <span className="text-foreground-500">({count})</span>
      )}
    </span>
  );
}

interface StarRatingInputProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

/** Selector interactivo de estrellas (1 a 5). */
export function StarRatingInput({
  value,
  onChange,
  disabled = false,
}: StarRatingInputProps) {
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Calificación">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          role="radio"
          aria-checked={value === star}
          aria-label={`${star} estrella${star > 1 ? "s" : ""}`}
          disabled={disabled}
          onClick={() => onChange(star)}
          className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-accent-100 disabled:cursor-not-allowed ${
            star <= value ? "text-accent-500" : "text-foreground-300"
          }`}
        >
          <i
            className={`text-2xl ${star <= value ? "ri-star-fill" : "ri-star-line"}`}
          ></i>
        </button>
      ))}
    </div>
  );
}